import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  query,
  where
} from 'firebase/firestore';
import { db } from './config';

// اسم المجموعة في Firestore
const IMAGES_COLLECTION = 'images';
const PROFILE_COLLECTION = 'profile';
const CATEGORIES_COLLECTION = 'categories';
const CONTACT_COLLECTION = 'settings';
const MESSAGES_COLLECTION = 'messages';
const TOPICS_COLLECTION = 'topics';

// جلب جميع الصور المرئية (يتوافق مع قواعد الأمان: visible == true || مسجّل دخول)
export const getAllImages = async () => {
  try {
    const imagesCol = collection(db, IMAGES_COLLECTION);
    const q = query(imagesCol, where('visible', '==', true));
    const imageSnapshot = await getDocs(q);
    const imageList = imageSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return imageList;
  } catch (error) {
    console.error('Error fetching images:', error);
    throw error;
  }
};

// جلب صورة واحدة حسب ID
export const getImageById = async (imageId) => {
  try {
    const imageDoc = doc(db, IMAGES_COLLECTION, imageId);
    const imageSnapshot = await getDoc(imageDoc);
    
    if (imageSnapshot.exists()) {
      return {
        id: imageSnapshot.id,
        ...imageSnapshot.data()
      };
    } else {
      throw new Error('Image not found');
    }
  } catch (error) {
    console.error('Error fetching image:', error);
    throw error;
  }
};

// جلب الصور حسب التصنيف
export const getImagesByCategory = async (category) => {
  try {
    const imagesCol = collection(db, IMAGES_COLLECTION);
    const q = query(imagesCol, where('category', '==', category));
    const imageSnapshot = await getDocs(q);
    const imageList = imageSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return imageList;
  } catch (error) {
    console.error('Error fetching images by category:', error);
    throw error;
  }
};

// جلب بيانات الملف الشخصي
export const getProfile = async () => {
  try {
    const profileCol = collection(db, PROFILE_COLLECTION);
    const profileSnapshot = await getDocs(profileCol);
    
    if (!profileSnapshot.empty) {
      const profileDoc = profileSnapshot.docs[0];
      return {
        id: profileDoc.id,
        ...profileDoc.data()
      };
    } else {
      throw new Error('Profile not found');
    }
  } catch (error) {
    console.error('Error fetching profile:', error);
    throw error;
  }
};

// إضافة صورة جديدة
export const addImage = async (imageData) => {
  try {
    const imagesCol = collection(db, IMAGES_COLLECTION);
    const docRef = await addDoc(imagesCol, imageData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding image:', error);
    throw error;
  }
};

// تحديث صورة
export const updateImage = async (imageId, imageData) => {
  try {
    const imageDoc = doc(db, IMAGES_COLLECTION, imageId);
    await updateDoc(imageDoc, imageData);
    return true;
  } catch (error) {
    console.error('Error updating image:', error);
    throw error;
  }
};

// تحديث صورة باستخدام filename
export const updateImageByFilename = async (filename, imageData) => {
  try {
    const imagesCol = collection(db, IMAGES_COLLECTION);
    const q = query(imagesCol, where('filename', '==', filename));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Image not found');
    }
    
    const imageDoc = snapshot.docs[0];
    await updateDoc(imageDoc.ref, imageData);
    return true;
  } catch (error) {
    console.error('Error updating image by filename:', error);
    throw error;
  }
};

// حذف صورة
export const deleteImage = async (imageId) => {
  try {
    const imageDoc = doc(db, IMAGES_COLLECTION, imageId);
    await deleteDoc(imageDoc);
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

// البحث في الصور
export const searchImages = async (searchTerm) => {
  try {
    const imagesCol = collection(db, IMAGES_COLLECTION);
    const imageSnapshot = await getDocs(imagesCol);
    const imageList = imageSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // فلترة النتائج محلياً (لأن Firestore لا يدعم البحث النصي الكامل)
    const filteredImages = imageList.filter(image => 
      image.caption?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      image.extractedText?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return filteredImages;
  } catch (error) {
    console.error('Error searching images:', error);
    throw error;
  }
};

// ========== وظائف الزوار ==========

// الحصول على معلومات الموقع من IP
const getLocationFromIP = async () => {
  // محاولة استخدام عدة APIs احتياطية
  const apis = [
    {
      url: 'https://ipapi.co/json/',
      parser: (data) => ({
        country: data.country_name || 'غير محدد',
        countryCode: data.country_code || 'XX',
        city: data.city || 'غير محدد',
        region: data.region || 'غير محدد'
      })
    },
    {
      url: 'https://api.ipify.org?format=json',
      parser: async (data) => {
        // الحصول على IP ثم استخدام ip-api.com
        const ipResponse = await fetch(`https://ip-api.com/json/${data.ip}`);
        const ipData = await ipResponse.json();
        return {
          country: ipData.country || 'غير محدد',
          countryCode: ipData.countryCode || 'XX',
          city: ipData.city || 'غير محدد',
          region: ipData.regionName || 'غير محدد'
        };
      }
    },
    {
      url: 'https://ip-api.com/json/',
      parser: (data) => ({
        country: data.country || 'غير محدد',
        countryCode: data.countryCode || 'XX',
        city: data.city || 'غير محدد',
        region: data.regionName || 'غير محدد'
      })
    }
  ];

  // محاولة كل API حتى ينجح أحدها
  for (const api of apis) {
    try {
      const response = await fetch(api.url, { 
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) continue;
      
      const data = await response.json();
      const location = await api.parser(data);
      
      // التحقق من أن البيانات صحيحة
      if (location.countryCode && location.countryCode !== 'XX') {
        console.log('✅ تم الحصول على الموقع بنجاح:', location);
        return location;
      }
    } catch (error) {
      console.warn(`فشل API ${api.url}:`, error.message);
      continue;
    }
  }
  
  // إذا فشلت جميع المحاولات
  console.error('❌ فشل الحصول على الموقع من جميع APIs');
  return {
    country: 'غير محدد',
    countryCode: 'XX',
    city: 'غير محدد',
    region: 'غير محدد'
  };
};

// تسجيل زائر جديد
export const trackVisitor = async () => {
  try {
    // التحقق من وجود معرف فريد للزائر
    let visitorId = localStorage.getItem('visitorId');
    
    if (!visitorId) {
      // إنشاء معرف فريد جديد
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('visitorId', visitorId);
      
      // الحصول على موقع الزائر
      const location = await getLocationFromIP();
      
      // حفظ الزائر الجديد في Firebase
      const visitorsDoc = doc(db, 'analytics', 'visitors');
      const docSnap = await getDoc(visitorsDoc);
      
      const visitorData = {
        id: visitorId,
        firstVisit: new Date().toISOString(),
        country: location.country,
        countryCode: location.countryCode,
        city: location.city,
        region: location.region
      };
      
      if (docSnap.exists()) {
        const currentCount = docSnap.data().count || 0;
        const visitors = docSnap.data().visitors || [];
        const countries = docSnap.data().countries || {};
        
        // تحديث عدد الزوار من كل دولة
        const countryCount = countries[location.countryCode] || 0;
        
        // إضافة الزائر الجديد
        await updateDoc(visitorsDoc, {
          count: currentCount + 1,
          visitors: [...visitors, visitorData],
          countries: {
            ...countries,
            [location.countryCode]: countryCount + 1
          },
          lastUpdated: new Date().toISOString()
        });
      } else {
        // إنشاء المستند للمرة الأولى
        await setDoc(visitorsDoc, {
          count: 1,
          visitors: [visitorData],
          countries: {
            [location.countryCode]: 1
          },
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        });
      }
    }
    
    return visitorId;
  } catch (error) {
    console.error('Error tracking visitor:', error);
    return null;
  }
};

// جلب عدد الزوار
export const getVisitorCount = async () => {
  try {
    const visitorsDoc = doc(db, 'analytics', 'visitors');
    const docSnap = await getDoc(visitorsDoc);
    
    if (docSnap.exists()) {
      return docSnap.data().count || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error getting visitor count:', error);
    return 0;
  }
};

// جلب إحصائيات الدول
export const getCountriesStats = async () => {
  try {
    const visitorsDoc = doc(db, 'analytics', 'visitors');
    const docSnap = await getDoc(visitorsDoc);
    
    if (docSnap.exists()) {
      const countries = docSnap.data().countries || {};
      const visitors = docSnap.data().visitors || [];
      
      // تحويل البيانات إلى مصفوفة مع أسماء الدول
      const countryNames = {
        'SA': 'السعودية',
        'EG': 'مصر',
        'AE': 'الإمارات',
        'KW': 'الكويت',
        'QA': 'قطر',
        'BH': 'البحرين',
        'OM': 'عمان',
        'JO': 'الأردن',
        'LB': 'لبنان',
        'SY': 'سوريا',
        'IQ': 'العراق',
        'YE': 'اليمن',
        'PS': 'فلسطين',
        'IL': 'إسرائيل',
        'MA': 'المغرب',
        'DZ': 'الجزائر',
        'TN': 'تونس',
        'LY': 'ليبيا',
        'SD': 'السودان',
        'MR': 'موريتانيا',
        'SO': 'الصومال',
        'DJ': 'جيبوتي',
        'KM': 'جزر القمر',
        'US': 'الولايات المتحدة',
        'GB': 'بريطانيا',
        'FR': 'فرنسا',
        'DE': 'ألمانيا',
        'IT': 'إيطاليا',
        'ES': 'إسبانيا',
        'PT': 'البرتغال',
        'NL': 'هولندا',
        'BE': 'بلجيكا',
        'CH': 'سويسرا',
        'AT': 'النمسا',
        'SE': 'السويد',
        'NO': 'النرويج',
        'DK': 'الدنمارك',
        'FI': 'فنلندا',
        'PL': 'بولندا',
        'CZ': 'التشيك',
        'GR': 'اليونان',
        'TR': 'تركيا',
        'RU': 'روسيا',
        'UA': 'أوكرانيا',
        'CN': 'الصين',
        'JP': 'اليابان',
        'KR': 'كوريا الجنوبية',
        'IN': 'الهند',
        'PK': 'باكستان',
        'BD': 'بنغلاديش',
        'ID': 'إندونيسيا',
        'MY': 'ماليزيا',
        'SG': 'سنغافورة',
        'TH': 'تايلاند',
        'VN': 'فيتنام',
        'PH': 'الفلبين',
        'AU': 'أستراليا',
        'NZ': 'نيوزيلندا',
        'CA': 'كندا',
        'MX': 'المكسيك',
        'BR': 'البرازيل',
        'AR': 'الأرجنتين',
        'CL': 'تشيلي',
        'CO': 'كولومبيا',
        'PE': 'بيرو',
        'VE': 'فنزويلا',
        'ZA': 'جنوب أفريقيا',
        'NG': 'نيجيريا',
        'KE': 'كينيا',
        'ET': 'إثيوبيا',
        'GH': 'غانا',
        'TZ': 'تنزانيا',
        'UG': 'أوغندا',
        'ZW': 'زيمبابوي',
        'MZ': 'موزمبيق',
        'AO': 'أنغولا',
        'IR': 'إيران',
        'AF': 'أفغانستان',
        'AZ': 'أذربيجان',
        'KZ': 'كازاخستان',
        'UZ': 'أوزبكستان',
        'TM': 'تركمانستان',
        'KG': 'قيرغيزستان',
        'TJ': 'طاجيكستان',
        'AM': 'أرمينيا',
        'GE': 'جورجيا',
        'AL': 'ألبانيا',
        'BA': 'البوسنة والهرسك',
        'HR': 'كرواتيا',
        'RS': 'صربيا',
        'BG': 'بلغاريا',
        'RO': 'رومانيا',
        'HU': 'المجر',
        'SK': 'سلوفاكيا',
        'SI': 'سلوفينيا',
        'MK': 'مقدونيا الشمالية',
        'ME': 'الجبل الأسود',
        'XK': 'كوسوفو',
        'IE': 'أيرلندا',
        'IS': 'آيسلندا',
        'LU': 'لوكسمبورغ',
        'MT': 'مالطا',
        'CY': 'قبرص',
        'EE': 'إستونيا',
        'LV': 'لاتفيا',
        'LT': 'ليتوانيا',
        'BY': 'بيلاروسيا',
        'MD': 'مولدوفا',
        'XX': 'غير معروف'
      };
      
      const countriesArray = Object.entries(countries).map(([code, count]) => ({
        code,
        name: countryNames[code] || code,
        count
      })).sort((a, b) => b.count - a.count);
      
      return countriesArray;
    }
    return [];
  } catch (error) {
    console.error('Error getting countries stats:', error);
    return [];
  }
};

// ========== وظائف التصنيفات ==========

// جلب التصنيفات
export const getCategories = async () => {
  try {
    const categoriesDoc = doc(db, CATEGORIES_COLLECTION, 'list');
    const docSnap = await getDoc(categoriesDoc);
    
    if (docSnap.exists()) {
      return docSnap.data().categories || [];
    } else {
      // إرجاع التصنيفات الافتراضية إذا لم توجد في Firebase
      const defaultCategories = [
        'الفقه',
        'العقيدة',
        'الحديث',
        'التفسير وعلوم القرآن',
        'السيرة النبوية',
        'السير والتراجم',
        'الرقائق',
        'الآداب والأخلاق',
        'الوسواس القهري',
        'وصايا علمية',
        'متفرقات'
      ];
      // حفظ التصنيفات الافتراضية
      await saveCategories(defaultCategories);
      return defaultCategories;
    }
  } catch (error) {
    console.error('Error getting categories:', error);
    throw error;
  }
};

// حفظ التصنيفات
export const saveCategories = async (categories) => {
  try {
    const categoriesDoc = doc(db, CATEGORIES_COLLECTION, 'list');
    await updateDoc(categoriesDoc, {
      categories: categories,
      updatedAt: new Date().toISOString()
    }).catch(async (error) => {
      // إذا لم يكن المستند موجوداً، أنشئه
      if (error.code === 'not-found') {
        await setDoc(categoriesDoc, {
          categories: categories,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      } else {
        throw error;
      }
    });
    return true;
  } catch (error) {
    console.error('Error saving categories:', error);
    throw error;
  }
};

// ========== وظائف إدارة صفحة التواصل ==========

// جلب إعدادات صفحة التواصل
export const getContactSettings = async () => {
  try {
    const contactDoc = doc(db, CONTACT_COLLECTION, 'contact');
    const docSnap = await getDoc(contactDoc);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // إرجاع القيم الافتراضية إذا لم توجد بيانات
      return {
        visible: true,
        title: 'تواصل معنا',
        subtitle: 'نسعد بتواصلكم واستفساراتكم',
        email: 'admin@salman-alkhatep.com',
        twitter: {
          enabled: true,
          url: 'https://x.com/salman_alKhatep',
          handle: '@salman_alKhatep',
          description: 'تابعنا على تويتر للحصول على آخر التحديثات والفوائد اليومية'
        },
        youtube: {
          enabled: true,
          url: 'https://youtube.com/channel/UCJCc6TojI38et5ckeUikRWg?si=SV2ju6GjGLgrKHTB',
          handle: 'قناة سلمان الحران',
          description: 'اشترك في قناتنا على يوتيوب لمشاهدة الدروس والمحاضرات المرئية'
        },
        telegram: {
          enabled: true,
          url: 'https://t.me/salman_alkhatep',
          handle: '@salman_alkhatep',
          description: 'انضم إلى قناتنا على تلجرام للحصول على الفوائد والتصاميم الجديدة'
        },
        responseTime: {
          enabled: true,
          title: 'أوقات الرد',
          description: 'نسعى للرد على جميع الاستفسارات في أقرب وقت ممكن'
        },
        suggestions: {
          enabled: true,
          title: 'اقتراحاتكم',
          description: 'نرحب بجميع الاقتراحات والملاحظات لتطوير المحتوى'
        }
      };
    }
  } catch (error) {
    console.error('Error fetching contact settings:', error);
    throw error;
  }
};

// تحديث إعدادات صفحة التواصل
export const updateContactSettings = async (settings) => {
  try {
    const contactDoc = doc(db, CONTACT_COLLECTION, 'contact');
    await setDoc(contactDoc, {
      ...settings,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating contact settings:', error);
    throw error;
  }
};

// ========== وظائف إدارة صفحة "عن الشيخ" ==========

// جلب إعدادات صفحة "عن الشيخ"
export const getAboutSettings = async () => {
  try {
    const aboutDoc = doc(db, CONTACT_COLLECTION, 'about');
    const docSnap = await getDoc(aboutDoc);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // إرجاع القيم الافتراضية إذا لم توجد بيانات
      return {
        visible: true,
        pageTitle: 'عن الشيخ',
        profile: {
          enabled: true,
          name: 'سلمان بن عبدالله الحران',
          description: 'طالب علم متخصص في العلوم الشرعية، يسعى لنشر الفائدة والعلم النافع من خلال هذه السلسلة المباركة.'
        },
        series: {
          enabled: true,
          title: 'سلسلة الفوائد',
          description: 'سلسلة تعليمية تهدف إلى نشر الفوائد العلمية والمسائل الشرعية المختصرة في مختلف أبواب العلم الشرعي، في مواضيع متنوعة من الفقه والعقيدة والحديث والتفسير وغيرها.'
        },
        goals: {
          enabled: true,
          title: 'الأهداف',
          items: [
            'نشر العلم الشرعي الصحيح',
            'تبسيط المسائل العلمية للعامة',
            'توفير محتوى علمي موثوق ومختصر',
            'خدمة طلاب العلم والباحثين'
          ]
        },
        topics: {
          enabled: true,
          title: 'المجالات',
          items: ['العقيدة', 'الفقه', 'الحديث', 'التفسير', 'السيرة النبوية', 'الآداب والأخلاق']
        },
        contact: {
          enabled: true,
          title: 'تواصل معنا',
          twitter: 'https://x.com/salman_alKhatep',
          youtube: 'https://youtube.com/channel/UCJCc6TojI38et5ckeUikRWg?si=SV2ju6GjGLgrKHTB',
          telegram: 'https://t.me/salman_alkhatep'
        }
      };
    }
  } catch (error) {
    console.error('Error fetching about settings:', error);
    throw error;
  }
};

// تحديث إعدادات صفحة "عن الشيخ"
export const updateAboutSettings = async (settings) => {
  try {
    const aboutDoc = doc(db, CONTACT_COLLECTION, 'about');
    await setDoc(aboutDoc, {
      ...settings,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating about settings:', error);
    throw error;
  }
};

// ========== وظائف إدارة الرسائل ==========

// إرسال رسالة جديدة
export const sendMessage = async (messageData) => {
  try {
    const messagesCol = collection(db, MESSAGES_COLLECTION);
    const newMessage = {
      ...messageData,
      status: 'unread',
      createdAt: new Date().toISOString(),
      read: false
    };
    
    const docRef = await addDoc(messagesCol, newMessage);
    return docRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// جلب جميع الرسائل
export const getAllMessages = async () => {
  try {
    const messagesCol = collection(db, MESSAGES_COLLECTION);
    const messagesSnapshot = await getDocs(messagesCol);
    const messagesList = messagesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // ترتيب الرسائل حسب التاريخ (الأحدث أولاً)
    return messagesList.sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

// تحديث حالة الرسالة (مقروءة/غير مقروءة)
export const updateMessageStatus = async (messageId, status) => {
  try {
    const messageDoc = doc(db, MESSAGES_COLLECTION, messageId);
    await updateDoc(messageDoc, {
      status: status,
      read: status === 'read',
      readAt: status === 'read' ? new Date().toISOString() : null
    });
    return true;
  } catch (error) {
    console.error('Error updating message status:', error);
    throw error;
  }
};

// حذف رسالة
export const deleteMessage = async (messageId) => {
  try {
    const messageDoc = doc(db, MESSAGES_COLLECTION, messageId);
    await deleteDoc(messageDoc);
    return true;
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

// الحصول على عدد الرسائل غير المقروءة
export const getUnreadMessagesCount = async () => {
  try {
    const messagesCol = collection(db, MESSAGES_COLLECTION);
    const q = query(messagesCol, where('read', '==', false));
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread messages count:', error);
    return 0;
  }
};

// الحصول على رقم تسلسلي للزائر (بناءً على عدد الرسائل + 1)
export const getNextVisitorNumber = async () => {
  try {
    const messagesCol = collection(db, MESSAGES_COLLECTION);
    const messagesSnapshot = await getDocs(messagesCol);
    return messagesSnapshot.size + 1;
  } catch (error) {
    console.error('Error getting visitor number:', error);
    return Math.floor(Math.random() * 10000) + 1; // fallback للرقم العشوائي
  }
};
